import { createContext, useContext, useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import * as offline from "@/lib/offline";
import { toast } from "sonner";

const OfflineContext = createContext(null);
export const useOffline = () => useContext(OfflineContext);

// Maps a queued action type -> the API call that replays it.
const REPLAY = {
  "clock-in": (s, p) => api.workerClockIn(s, p),
  "clock-out": (s, p) => api.workerClockOut(s, p),
  "note": (s, p) => api.workerSaveNote(s, p.participant, p.fields),
  "submit-notes": (s) => api.workerSubmitNotes(s),
  "km": (s, p) => api.workerKm(s, p),
  "task": (s, p) => api.workerTask(s, p.index, p.completion_status, p.not_done_reason),
  "incident": (s, p) => api.workerAddIncident(s, p),
};

export function OfflineProvider({ children }) {
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [pending, setPending] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const syncingRef = useRef(false);

  const refresh = useCallback(async () => {
    try { setPending(await offline.countPending()); } catch { /* ignore */ }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const enqueue = useCallback(async (type, shiftName, payload) => {
    await offline.enqueue(type, shiftName, payload);
    await refresh();
  }, [refresh]);

  const syncNow = useCallback(async (opts = {}) => {
    if (syncingRef.current) return { done: 0, failed: 0 };
    if (typeof navigator !== "undefined" && !navigator.onLine) {
      if (!opts.silent) toast.error("Still offline — can't sync yet");
      return { done: 0, failed: 0 };
    }
    syncingRef.current = true;
    setSyncing(true);
    let done = 0, failed = 0, dropped = 0;
    try {
      const items = await offline.getPending();
      for (const it of items) {
        if (it.corrupt) { await offline.remove(it.id); dropped += 1; continue; }
        const fn = REPLAY[it.type];
        if (!fn) { await offline.remove(it.id); continue; }
        try {
          await fn(it.shiftName, it.payload);
          await offline.remove(it.id);
          done += 1;
        } catch (e) {
          if (offline.isOfflineError(e)) { failed += 1; break; } // lost connection mid-sync
          // Server rejected it (validation/duplicate) — drop so the queue can drain.
          await offline.markError(it.id, e.response?.data?.detail || e.message);
          await offline.remove(it.id);
          dropped += 1;
        }
      }
    } finally {
      await refresh();
      syncingRef.current = false;
      setSyncing(false);
    }
    if (dropped) toast.error(`${dropped} offline change${dropped > 1 ? "s" : ""} couldn't be saved and ${dropped > 1 ? "were" : "was"} discarded — please re-check that shift.`);
    if (!opts.silent) {
      if (done) toast.success(`Synced ${done} offline change${done > 1 ? "s" : ""}`);
      else if (!failed && !dropped) toast.success("Everything is already synced");
    } else if (done) {
      toast.success(`Synced ${done} offline change${done > 1 ? "s" : ""}`);
    }
    return { done, failed, dropped };
  }, [refresh]);

  // React to connectivity changes; auto-sync when we come back online.
  useEffect(() => {
    const onOnline = () => { setOnline(true); syncNow({ silent: true }); };
    const onOffline = () => setOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => { window.removeEventListener("online", onOnline); window.removeEventListener("offline", onOffline); };
  }, [syncNow]);

  return (
    <OfflineContext.Provider value={{ online, pending, syncing, syncNow, enqueue, refresh }}>
      {children}
    </OfflineContext.Provider>
  );
}
